/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  MessageSquare, 
  Building2, 
  AlertCircle, 
  Loader2,
  TrendingUp
} from 'lucide-react';
import { dbService } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import WarmRequestsTab from '../components/dashboard/WarmRequestsTab';
import MessagesTab from '../components/dashboard/MessagesTab';
import { ConversationWithDetails } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import { encryptId } from '../utils/encryption';
import { RealtimeChannel } from '@supabase/supabase-js';

interface DashboardStats {
  accepted: number;
  pending: number;
  declined: number;
  total: number;
}

interface StartupDashboardPageProps {
  initialTab?: 'warm-intro-status' | 'messages';
  validatedConversationId?: string | null;
  isVerifyingAccess?: boolean;
}

const StartupDashboardPage: React.FC<StartupDashboardPageProps> = ({ 
  initialTab = 'warm-intro-status',
  validatedConversationId = null,
  isVerifyingAccess = false
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'warm-intro-status' | 'messages'>(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    accepted: 0,
    pending: 0,
    declined: 0,
    total: 0
  });
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(validatedConversationId);
  const [messagesUnreadCount, setMessagesUnreadCount] = useState(0);
  const [conversations, setConversations] = useState<ConversationWithDetails[]>([]);
  const subscriptionRef = useRef<RealtimeChannel | null>(null);

  // Update selected conversation and tab when validated ID changes
  useEffect(() => {
    if (validatedConversationId) {
      console.log('🎯 ConversationGuard provided conversation ID, switching to messages tab:', validatedConversationId);
      setSelectedConversationId(validatedConversationId);
      setActiveTab('messages');
    } else {
      setSelectedConversationId(null);
    }
  }, [validatedConversationId]);

  // Update URL when tab changes
  const handleTabChange = (tab: 'warm-intro-status' | 'messages') => {
    setActiveTab(tab);
    
    if (tab === 'messages') {
      navigate('/startup-dashboard/messages', { replace: true });
    } else {
      navigate('/startup-dashboard', { replace: true });
    }
  };

  // Update URL when conversation is selected
  const handleConversationSelect = (conversation: ConversationWithDetails) => {
    setSelectedConversationId(conversation.id);
    
    // If this conversation has unread messages, mark them as read and update count
    if (conversation.unread_count && conversation.unread_count > 0) {
      setMessagesUnreadCount(prev => Math.max(0, prev - (conversation.unread_count || 0)));
    }

    // Update URL with encrypted conversation ID
    const encryptedId = encryptId(conversation.id);
    navigate(`/startup-dashboard/messages/${encryptedId}`, { replace: true });
  };

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user?.id]);

  // Real-time subscription for unread message counts
  // Note: Removed independent unread count subscription to prevent double-counting
  // The MessagesTab component now handles all unread count updates via onUnreadCountChange callback

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load user's introduction requests
      const requests = await dbService.getUserIntroductionRequests(user!.id);
      
      // Calculate stats
      const stats = {
        accepted: requests.filter(r => r.status === 'accepted').length,
        pending: requests.filter(r => r.status === 'pending').length,
        declined: requests.filter(r => r.status === 'declined').length,
        total: requests.length
      };
      
      setStats(stats);

      // Load unread message count
      if (user?.id) {
        const conversations = await dbService.getUserConversations(user.id);
        setConversations(conversations);
        const totalUnread = conversations.reduce((total, conv) => total + (conv.unread_count || 0), 0);
        setMessagesUnreadCount(totalUnread);
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  // Real-time subscription for introduction request updates
  useEffect(() => {
    if (!user?.id) return;

    // Cleanup existing subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }


    // Create real-time subscription for introduction requests
    const channelName = `startup-intro-requests-${user.id}-${Date.now()}`;
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'introduction_requests',
          filter: `requester_user_id=eq.${user.id}` // Only listen to this user's requests
        },
        async () => {
          try {
            // Reload dashboard data to update stats
            await loadDashboardData();
          } catch (error) {
            console.error('❌ Error updating dashboard data:', error);
          }
        }
      )
      .subscribe();

    subscriptionRef.current = subscription;

    // Cleanup on unmount
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [user?.id, loadDashboardData]);

  // Real-time subscription for message count updates (works when MessagesTab is not mounted)
  // This only updates the count, not the conversation details to prevent double-counting
  useEffect(() => {
    if (!user?.id) return;

    console.log('🔔 Setting up lightweight message count subscription for startup dashboard');

    const messageSubscription = supabase
      .channel(`startup-message-count:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        async (payload) => {
          const newMessage = payload.new as {
            sender_id: string;
            conversation_id: string;
          };

          // Skip if this is our own message
          if (newMessage.sender_id === user.id) {
            return;
          }

          // Check if this message is from one of our conversations
          const isOurConversation = conversations.some(conv => conv.id === newMessage.conversation_id);
          if (!isOurConversation) {
            return;
          }

          console.log('📬 New message count update in startup dashboard:', newMessage.conversation_id);

          // Only update the total count, don't modify conversations (MessagesTab handles that)
          setMessagesUnreadCount(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      console.log('🔕 Cleaning up startup message count subscription');
      messageSubscription.unsubscribe();
    };
  }, [user?.id, conversations]);


  const handleStartConversation = async (introductionRequestId: string) => {
    try {
      if (!user?.id) {
        console.error('User not authenticated');
        return;
      }

      // Get the introduction request details
      const requests = await dbService.getUserIntroductionRequests(user.id);
      const request = requests.find(r => r.id === introductionRequestId);
      
      if (!request || !request.cdmo_applications?.id) {
        console.error('Request not found or CDMO details missing');
        return;
      }

      // Get CDMO user ID from the CDMO application
      const cdmoApplication = await dbService.getCDMOApplicationById(request.cdmo_applications.id);
      if (!cdmoApplication?.user_id) {
        console.error('CDMO user not found');
        return;
      }

      // Get or create conversation
      const conversation = await dbService.getOrCreateConversation(
        introductionRequestId,
        user.id,
        cdmoApplication.user_id,
        request.cdmo_applications.id
      );

      // Navigate to messages tab with encrypted conversation ID
      const encryptedId = encryptId(conversation.id);
      navigate(`/startup-dashboard/messages/${encryptedId}`);
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  // Function to handle unread count changes from MessagesTab
  const handleUnreadCountChange = (conversationId: string, newCount: number) => {
    // Update the conversations state with the new unread count
    setConversations(prev => {
      const updatedConversations = prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, unread_count: newCount }
          : conv
      );
      
      // Recalculate total unread count from updated conversations
      const totalUnread = updatedConversations.reduce((total, conv) => total + (conv.unread_count || 0), 0);
      setMessagesUnreadCount(totalUnread);
      
      return updatedConversations;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-electric-blue animate-spin mx-auto mb-4" />
          <p className="text-slate-gray">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadDashboardData}
            className="px-4 py-2 bg-electric-blue text-white rounded-lg hover:bg-electric-blue/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="bg-gradient-to-br from-electric-blue/20 to-deep-indigo/15 backdrop-blur-sm border-b border-electric-blue/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center">
              <div className="w-12 h-12 shrink-0 bg-gradient-primary rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <Building2 className="w-6 h-6 shrink-0 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-charcoal">Dashboard</h1>
                <p className="text-slate-gray">
                  Manage your CDMO connection and conversations
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Space after header */}
      <div className="mt-6 sm:mt-8" />

      {/* Dashboard Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex justify-center mt-6 sm:mt-8 mb-8"
      >
        <div className="inline-flex rounded-2xl bg-white/95 backdrop-blur-sm shadow-lg border border-white/50 overflow-hidden">
          {/* Warm Intro Tab */}
          <button
            onClick={() => handleTabChange('warm-intro-status')}
            className={`px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
              activeTab === 'warm-intro-status'
                ? 'bg-electric-blue text-white shadow-md'
                : 'text-slate-gray hover:text-charcoal hover:bg-slate-gray/5'
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            <span className="hidden sm:inline">Warm Intro Status</span>
            <span className="sm:hidden">Warm Intro</span>
          </button>

          {/* Messages Tab */}
          <button
            onClick={() => handleTabChange('messages')}
            className={`px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 relative ${
              activeTab === 'messages'
                ? 'bg-electric-blue text-white shadow-md'
                : 'text-slate-gray hover:text-charcoal hover:bg-slate-gray/5'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            <span>Messages</span>
            {messagesUnreadCount > 0 && (
              <span className="-right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] flex items-center justify-center">
                {messagesUnreadCount > 9 ? '9+' : messagesUnreadCount}
              </span>
            )}
          </button>
        </div>
      </motion.div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'warm-intro-status' && (
          <motion.div
            key="warm-intro-status"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            className="p-4 sm:p-6"
          >
            <WarmRequestsTab
              stats={stats}
              onStartConversation={handleStartConversation}
              onDataUpdate={loadDashboardData}
              highlightRequestId={searchParams.get('requestId')}
            />
          </motion.div>
        )}

        {activeTab === 'messages' && (
          <motion.div
            key="messages"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            className="p-4 sm:p-6"
          >
            <MessagesTab 
              selectedConversationId={selectedConversationId}
              onConversationSelect={handleConversationSelect}
              onUnreadCountChange={handleUnreadCountChange}
              isVerifyingAccess={isVerifyingAccess}
              totalUnreadCount={messagesUnreadCount}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StartupDashboardPage;
